/**
 * Bidirectional field mapping between Supabase row data and the Meeting interface.
 * The Meeting interface uses Spanish/Catalan Airtable field names.
 * Supabase uses English snake_case column names.
 */
import { Meeting, ProcessedMessage, FileAttachment } from '../../shared/types';
import { SupabaseMeetingWithLead, SupabaseMessageRow, SupabaseExportMeeting, SupabaseExportLead } from './types';

/**
 * Transform a Supabase meeting+lead row into the Meeting interface
 */
export function supabaseRowToMeeting(row: SupabaseMeetingWithLead): Meeting {
  const lead = row.inbox_leads;

  // Build location string from city/state/country
  const locationParts = [
    lead?.company_city,
    lead?.company_state,
    lead?.company_country,
  ].filter(Boolean);

  return {
    id: row.id,
    fields: {
      Nombre: lead?.full_name || '',
      Empresa: lead?.company_name || '',
      Email: lead?.sender_email || undefined,
      'Teléfono': lead?.phone_number || undefined,
      'Teléfono móvil': lead?.mobile_number || undefined,
      Linkedin: lead?.linkedin_profile || undefined,
      'Página Web': lead?.company_domain || undefined,
      'Localización': locationParts.length > 0 ? locationParts.join(', ') : undefined,
      Sector: lead?.company_industry || undefined,
      Empleados: lead?.company_employees || undefined,
      'Facturación': lead?.company_revenue || undefined,
      'Fecha Reunión': row.meeting_date || '',
      'Status Lead': row.status_lead || null,
      Notas: row.notes || undefined,
      notes_updated_at: row.notes_updated_at ?? null,
      notes_updated_by: row.notes_updated_by ?? null,
      'Volver a contactar': row.follow_up_date || null,
      'Seguimiento Sol Media': row.sol_media_followup || undefined,
      Account: row.accounts?.account_name || undefined,
      is_direct_call: row.is_direct_call ?? false,
      meeting_type: row.meeting_type ?? 'meeting',
      was_transfer: row.was_transfer ?? false,
      transfer_status: row.transfer_status ?? null,
      promoted_at: row.promoted_at ?? null,
      // Synthetic: lead_id is used by Supabase conversation loader
      'Link to Missatges (from Link Inbox)': row.lead_id ? [row.lead_id] : undefined,
      // Calendly questions: jsonb → string for display
      Resumen: undefined,
      Conversación: undefined,
      'Conversa HTML 2': undefined,
    },
  };
}

// Meeting fields that belong to inbox_meetings table
const MEETING_TABLE_FIELDS: Record<string, string> = {
  'Fecha Reunión': 'meeting_date',
  'Status Lead': 'status_lead',
  'Notas': 'notes',
  notes_updated_at: 'notes_updated_at',
  notes_updated_by: 'notes_updated_by',
  'Volver a contactar': 'follow_up_date',
  'Seguimiento Sol Media': 'sol_media_followup',
  // Promote action targets — written by the Interested-tab promote button.
  // is_direct_call is co-written by the caller to stay in sync with
  // meeting_type ('direct_call' → true, otherwise false). promoted_at is
  // stamped by the caller on transition out of 'interested_transfer'.
  meeting_type: 'meeting_type',
  is_direct_call: 'is_direct_call',
  promoted_at: 'promoted_at',
  transfer_status: 'transfer_status',
};

// Meeting fields that belong to inbox_leads table
const LEAD_TABLE_FIELDS: Record<string, string> = {
  'Nombre': 'full_name',
  'Empresa': 'company_name',
  'Email': 'sender_email',
  'Teléfono': 'phone_number',
  'Teléfono móvil': 'mobile_number',
  'Linkedin': 'linkedin_profile',
  'Página Web': 'company_domain',
  'Localización': 'company_city', // simplified: only maps to city
  'Sector': 'company_industry',
  'Empleados': 'company_employees',
  'Facturación': 'company_revenue',
  'Title': 'job_title',
};

/**
 * Split Meeting fields update into Supabase table-specific updates
 */
export function splitFieldsForUpdate(fields: Partial<Meeting['fields']>): {
  meetingUpdates: Record<string, unknown>;
  leadUpdates: Record<string, unknown>;
} {
  const meetingUpdates: Record<string, unknown> = {};
  const leadUpdates: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(fields)) {
    if (key in MEETING_TABLE_FIELDS) {
      meetingUpdates[MEETING_TABLE_FIELDS[key]] = value ?? null;
    } else if (key in LEAD_TABLE_FIELDS) {
      leadUpdates[LEAD_TABLE_FIELDS[key]] = value ?? null;
    }
    // Ignore fields that don't map (Account, Link to Missatges, etc.)
  }

  return { meetingUpdates, leadUpdates };
}

/**
 * Parse file attachments from a string (Airtable "Fitxers" format or similar)
 */
function parseAttachments(attachmentsStr: string | null): FileAttachment[] {
  if (!attachmentsStr) return [];
  try {
    const parsed = JSON.parse(attachmentsStr);
    if (Array.isArray(parsed)) {
      return parsed.map((a: { filename?: string; url?: string }) => ({
        name: a.filename || 'attachment',
        url: a.url || '',
      }));
    }
  } catch {
    // Not JSON, ignore
  }
  return [];
}

/**
 * Generate initials from a name
 */
function getInitials(name: string): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0].substring(0, 2).toUpperCase();
}

/**
 * Format a timestamp to Madrid timezone display
 */
function formatTimestamp(timestamp: string | null): string {
  if (!timestamp) return '';
  try {
    return new Date(timestamp).toLocaleString('es-ES', {
      timeZone: 'Europe/Madrid',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return timestamp;
  }
}

/**
 * Transform a Supabase inbox_messages row into ProcessedMessage
 */
export function supabaseMessageToProcessed(msg: SupabaseMessageRow): ProcessedMessage {
  const isSent = msg.message_type === 'SENT';
  const senderName = msg.sender_name || '';
  const senderEmail = msg.sender_email || '';

  return {
    id: msg.id,
    type: msg.message_type || 'REPLY',
    senderName,
    senderEmail,
    displayName: senderName || senderEmail,
    initials: getInitials(senderName || senderEmail),
    isCompanyMessage: isSent,
    direction: isSent ? 'outgoing' : 'incoming',
    recipients: msg.recipients_to || msg.recipient_email || '',
    cc: msg.cc_recipients || '',
    cco: '',
    messageText: msg.body_html || msg.body_text || msg.cleaned_message || '',
    formattedTimestamp: formatTimestamp(msg.timestamp),
    timestampRaw: msg.timestamp || '',
    subject: msg.subject || '',
    attachments: parseAttachments(msg.attachments),
    messageNote: msg.message_note || undefined,
  };
}

/**
 * Export rows keep the old Airtable field names: exportUtils' column mappings
 * (EXPORT_COLUMNS / INBOX_EXPORT_COLUMNS) are keyed on them.
 */
export function supabaseRowToExportMeeting(row: SupabaseExportMeeting): { id: string; fields: Record<string, unknown> } {
  const lead = row.inbox_leads;
  return {
    id: row.id,
    fields: {
      Nombre: lead?.full_name ?? '',
      Empresa: lead?.company_name ?? '',
      'Status Lead': row.status_lead ?? '',
      'Fecha Reunión': row.meeting_date ?? '',
      'Teléfono': lead?.phone_number || lead?.mobile_number || '',
      Email: lead?.sender_email ?? '',
      Ciutat: lead?.company_city ?? '',
      Pais: lead?.company_country ?? '',
      'Página Web': lead?.company_domain ?? '',
      Sector: lead?.company_industry ?? '',
      Linkedin: lead?.linkedin_profile ?? '',
      'Facturación': lead?.company_revenue ?? '',
      Empleados: lead?.company_employees ?? '',
      Conversación: row.conversation ?? '',
      Notas: row.notes ?? '',
    },
  };
}

export function supabaseLeadToExportRecord(lead: SupabaseExportLead): { id: string; fields: Record<string, unknown> } {
  return {
    id: lead.id,
    fields: {
      Titol: lead.job_title ?? '',
      'Correu Lead': lead.sender_email ?? '',
      Telefon: lead.phone_number || lead.mobile_number || '',
      Nom: lead.first_name || lead.full_name || '',
      Cognom: lead.last_name ?? '',
      'Nom Empresa': lead.company_name ?? '',
      Pais: lead.company_country ?? '',
      Domini: lead.company_domain ?? '',
      Facturacio: lead.company_revenue ?? '',
      Linkedin: lead.linkedin_profile ?? '',
      'Linkedin Empresa': lead.linkedin_company ?? '',
      'Data Ultim missatge conversa': lead.last_message_date ?? '',
      'Status Lead (Inbox manager)': lead.status_lead_inbox_manager ?? '',
      Conversa: lead.conversation ?? '',
      Empleats: lead.company_employees ?? '',
    },
  };
}
